// Comisiones MP — listado de pagos que pasaron por Mercado Pago.
// Calcula comisión estimada según tipo (TC, TD, ticket, transfer, wallet).

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

// Comisión estimada (TEST, ajustar para producción real con datos del extracto MP)
const COMISIONES = {
  credit_card: 0.0599,    // TC inmediata
  debit_card:  0.0399,    // TD inmediata
  ticket:      0.0499,    // Abitab / RedPagos
  bank_transfer: 0.0299,
  account_money: 0.0299,  // MP wallet
  default: 0.0599,
}

const PM_LABEL = {
  credit_card: '💳 TC',
  debit_card: '💳 TD',
  ticket: '🎫 Abitab/RedPagos',
  bank_transfer: '🏦 Transferencia',
  account_money: '💼 MP wallet',
  visa: '💳 Visa',
  master: '💳 Master',
  oca: '💳 OCA',
  cabal: '💳 Cabal',
  amex: '💳 Amex',
  pix: '💸 PIX',
  rapipago: '🎫 Rapipago',
  pagofacil: '🎫 Pago Fácil',
  bna: '🏦 BNA',
}

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

function comisionPct(metodo) {
  if (!metodo) return COMISIONES.default
  const m = String(metodo).toLowerCase()
  if (m.includes('credit') || m === 'visa' || m === 'master' || m === 'oca' || m === 'amex' || m === 'cabal') return COMISIONES.credit_card
  if (m.includes('debit')) return COMISIONES.debit_card
  if (m.includes('ticket') || m.includes('abitab') || m.includes('redpagos') || m.includes('rapipago') || m.includes('pagofacil')) return COMISIONES.ticket
  if (m.includes('bank') || m.includes('transfer')) return COMISIONES.bank_transfer
  if (m.includes('wallet') || m.includes('account')) return COMISIONES.account_money
  return COMISIONES.default
}

export default function ComisionesMP() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [meses, setMeses] = useState(3)
  const [pagos, setPagos] = useState([])
  const [pedidos, setPedidos] = useState([])

  useEffect(() => { cargar() }, [meses])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const desde = new Date()
      desde.setMonth(desde.getMonth() - meses)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      const [{ data: pa, error: pe }, { data: pd, error: pde }] = await Promise.all([
        supabase
          .from('pagos')
          .select('id, monto, metodo, pagado_at, referencia_externa')
          .eq('estado', 'pagado')
          .gte('pagado_at', desdeStr),
        supabase
          .from('pedidos')
          .select('id, numero_orden, monto_total, metodo_pago, pagado_at, mp_payment_id, comprador_nombre')
          .in('estado', ['pagado', 'en_armado', 'en_ruta', 'entregado'])
          .gte('pagado_at', desdeStr),
      ])
      if (pe) throw pe
      if (pde) throw pde
      setPagos(pa || [])
      setPedidos(pd || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // Unifico todo en una sola lista
  const filas = useMemo(() => {
    const arr = []
    pagos.forEach(p => {
      const metodo = (p.metodo || '').toLowerCase()
      // Solo los que pasan por MP (suscripción)
      if (!metodo.startsWith('mp') && metodo !== 'tarjeta' && metodo !== 'mercadopago') return
      const pct = comisionPct(metodo)
      arr.push({
        id: 'pa_' + p.id,
        fuente: 'Suscripción',
        ref: p.referencia_externa || p.id.slice(0, 8),
        cliente: '—',
        fecha: p.pagado_at,
        monto: Number(p.monto || 0),
        metodo,
        comision_pct: pct,
        comision: Number(p.monto || 0) * pct,
      })
    })
    pedidos.forEach(p => {
      const metodo = (p.metodo_pago || '').toLowerCase()
      if (!metodo) return
      const pct = comisionPct(metodo)
      arr.push({
        id: 'pe_' + p.id,
        fuente: 'One-shot',
        ref: p.numero_orden || (p.mp_payment_id || p.id).toString().slice(0, 12),
        cliente: p.comprador_nombre || '—',
        fecha: p.pagado_at,
        monto: Number(p.monto_total || 0),
        metodo,
        comision_pct: pct,
        comision: Number(p.monto_total || 0) * pct,
      })
    })
    arr.sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0))
    return arr
  }, [pagos, pedidos])

  // Agregado por método de pago
  const porMetodo = useMemo(() => {
    const map = new Map()
    filas.forEach(r => {
      const k = r.metodo
      if (!map.has(k)) map.set(k, { metodo: k, n: 0, monto: 0, comision: 0, pct: r.comision_pct })
      const x = map.get(k)
      x.n += 1
      x.monto += r.monto
      x.comision += r.comision
    })
    return Array.from(map.values()).sort((a, b) => b.monto - a.monto)
  }, [filas])

  const totales = useMemo(() => {
    const t = { monto: 0, comision: 0, n: filas.length, neto: 0 }
    filas.forEach(r => { t.monto += r.monto; t.comision += r.comision })
    t.neto = t.monto - t.comision
    return t
  }, [filas])

  return (
    <AdminLayout>
      <div className="page">
        <h1>💳 Comisiones MP</h1>
        <p className="muted">Pagos procesados por Mercado Pago y comisión estimada por método. Útil para conciliación con el extracto MP.</p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            Período:
            <select value={meses} onChange={e => setMeses(Number(e.target.value))}
              style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value={1}>Último mes</option>
              <option value={3}>Últimos 3 meses</option>
              <option value={6}>Últimos 6 meses</option>
              <option value={12}>Último año</option>
            </select>
          </label>
        </div>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Transacciones MP</h3>
            <div className="stat">{totales.n}</div>
          </div>
          <div className="card">
            <h3>Bruto cobrado</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totales.monto)}</div>
          </div>
          <div className="card">
            <h3>Comisión MP est.</h3>
            <div className="stat" style={{ color: '#ef4444' }}>{fmtUYU(totales.comision)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              {totales.monto > 0 ? `${(totales.comision / totales.monto * 100).toFixed(2)}% promedio` : ''}
            </div>
          </div>
          <div className="card">
            <h3>Neto en cuenta MP</h3>
            <div className="stat" style={{ color: '#1a3461' }}>{fmtUYU(totales.neto)}</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>💳</div>
            <h3>Sin transacciones MP en el período</h3>
          </div>
        )}

        {!loading && porMetodo.length > 0 && (
          <>
            <h3 style={{ color: '#1a3461', margin: '24px 0 12px' }}>Resumen por método de pago</h3>
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 24 }}>
              <table className="usu-table">
                <thead>
                  <tr>
                    <th>Método</th>
                    <th style={{ textAlign: 'center' }}>Trans.</th>
                    <th style={{ textAlign: 'right' }}>Bruto</th>
                    <th style={{ textAlign: 'right' }}>Comisión est.</th>
                    <th style={{ textAlign: 'right' }}>%</th>
                    <th style={{ textAlign: 'right' }}>Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {porMetodo.map(r => (
                    <tr key={r.metodo}>
                      <td style={{ fontWeight: 600 }}>{PM_LABEL[r.metodo] || r.metodo}</td>
                      <td style={{ textAlign: 'center' }}>{r.n}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUYU(r.monto)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#ef4444' }}>−{fmtUYU(r.comision)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#6b7280', fontSize: 12 }}>{(r.pct * 100).toFixed(2)}%</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#10B981' }}>{fmtUYU(r.monto - r.comision)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && filas.length > 0 && (
          <>
            <h3 style={{ color: '#1a3461', margin: '0 0 12px' }}>Detalle de transacciones</h3>
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <table className="usu-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Fuente</th>
                    <th>Referencia</th>
                    <th>Cliente</th>
                    <th>Método</th>
                    <th style={{ textAlign: 'right' }}>Bruto</th>
                    <th style={{ textAlign: 'right' }}>Comisión</th>
                    <th style={{ textAlign: 'right' }}>Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.slice(0, 200).map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{fmtFecha(r.fecha)}</td>
                      <td style={{ fontSize: 12 }}>{r.fuente}</td>
                      <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#475569' }}>{r.ref}</td>
                      <td style={{ fontSize: 13 }}>{r.cliente}</td>
                      <td style={{ fontSize: 12 }}>{PM_LABEL[r.metodo] || r.metodo}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{fmtUYU(r.monto)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: '#ef4444' }}>−{fmtUYU(r.comision)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: '#10B981', fontWeight: 600 }}>{fmtUYU(r.monto - r.comision)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filas.length > 200 && (
                <div style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>
                  Mostrando 200 de {filas.length} — afiná el período para ver más
                </div>
              )}
            </div>
          </>
        )}

        <AnalisisIA
          reportType="comisiones_mp"
          data={{ totales, por_metodo: porMetodo, n_transacciones: filas.length }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Comisiones estimadas (TC ~5,99%, TD ~3,99%, Ticket ~4,99%, Wallet/Transfer ~2,99%).
          Los valores reales pueden variar según el plan y antigüedad de cuenta MP. Comparar con extracto oficial MP para conciliar.
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td {
          padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top;
        }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}
